import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Edit, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useBackButtonLogout } from '@/hooks/ea/useBackButtonLogout.ts';

import UnifiedHeader from '@/components/common/UnifiedHeader';
import LogoutConfirmDialog from '@/components/common/LogoutConfirmDialog.tsx';
import PaginationComponent from '@/components/common/PaginationComponent';
import PESearchForm from '@/components/pe/PESearchForm';
import { visiblePatientsService, MappedVisiblePatient, VisiblePatientsSearchParams } from '@/services/features/VisiblePatientsService';
import { useToast } from '@/hooks/use-toast';

interface PEDashboardProps {
  onNavigate?: (section: string, params?: any) => void;
  onPatientSelect?: (patientClinicDataId: number, clinicId?: number) => void;
}

const PEDashboard: React.FC<PEDashboardProps> = ({
  onNavigate,
  onPatientSelect
}) => {
  const { user, token, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  
  // Search state
  const [searchBy, setSearchBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');

  // Patient data state
  const [patients, setPatients] = useState<MappedVisiblePatient[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPatients = async (page: number = currentPage, searchParams?: VisiblePatientsSearchParams) => {
    if (!user?.id || !token) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      // API uses 0-based page index
      const apiPage = page - 1;
      
      const response = await visiblePatientsService.getVisiblePatients(
        Number(user.id),
        apiPage,
        rowsPerPage,
        token || undefined,
        searchParams
      );

      setPatients(response.patients);
      setTotalCount(response.totalItems);
      setTotalPages(response.totalPages);
      setCurrentPage(page);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch patients';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (user?.id && token) {
      // Load with current search params if any
      const searchParams: VisiblePatientsSearchParams | undefined = 
        searchTerm ? { searchBy, searchTerm } : undefined;
      loadPatients(1, searchParams);
    }
  }, [user?.id, token, rowsPerPage]);

  const handleRecordsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    // Maintain search filters during pagination
    const searchParams: VisiblePatientsSearchParams | undefined = 
      searchTerm ? { searchBy, searchTerm } : undefined;
    loadPatients(page, searchParams);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    const searchParams: VisiblePatientsSearchParams | undefined = 
      searchTerm ? { searchBy, searchTerm } : undefined;
    loadPatients(1, searchParams);
  };

  const handleReload = () => {
    setSearchTerm('');
    setSearchBy('name');
    loadPatients(1, undefined); // Clear search params on reload
  };

  // Check authentication on component mount
  useEffect(() => {
    if (!authLoading && (!user || !token)) {
      navigate('/', { replace: true });
      return;
    }

    if (user && !['PE'].includes(user.type)) {
      navigate('/', { replace: true });
      return;
    }
  }, [user, token, authLoading, navigate]);

  // Handle browser back button to show logout dialog
  useBackButtonLogout({
    onLogoutRequest: () => setShowLogoutDialog(true)
  });

  // Show loading during auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pe-dashboard-auth-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" data-testid="pe-dashboard-auth-spinner"></div>
      </div>
    );
  }

  // Don't render if user is not authenticated or doesn't have PE role
  if (!user || !token || !['PE'].includes(user.type)) {
    return null;
  }

  if (error) {
    return (
      <div className="text-center py-8" data-testid="pe-dashboard-error">
        <p className="text-red-500" data-testid="pe-dashboard-error-message">Error loading patients. Please try again.</p>
        <Button onClick={() => loadPatients(currentPage)} className="mt-4" data-testid="pe-dashboard-retry-button">
          <RotateCcw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="pe-dashboard">
      {/* Search and Filter Bar */}
      <div className="bg-white border-b py-4">
        <div className="flex items-center justify-between space-x-4">
          <PESearchForm
            searchBy={searchBy}
            searchTerm={searchTerm}
            onSearchByChange={setSearchBy}
            onSearchTermChange={setSearchTerm}
            onSearch={handleSearch}
            onReload={handleReload}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Patient Table */}
      <div className="py-6" data-testid="pe-patient-section">
        <Card className="bg-white rounded-lg" data-testid="pe-patient-table-card">
          <CardContent className="p-0" data-testid="pe-patient-table-content">
            {isLoading ? (
              <div className="flex items-center justify-center h-32" data-testid="pe-patient-table-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" data-testid="pe-loading-spinner"></div>
              </div>
            ) : patients.length === 0 ? (
              <div className="p-12 text-center" data-testid="pe-patient-table-empty">
                <h3 className="text-xl font-medium text-gray-900 mb-4" data-testid="pe-empty-message">
                  No patients found
                </h3>
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="pe-dashboard-table-wrapper">
                <Table id="pe-dashboard-patient-table" data-testid="pe-dashboard-patient-table">
                  <TableHeader data-testid="pe-dashboard-table-header">
                    <TableRow className="bg-muted/50 border-b-2 border-border">
                      <TableHead className="font-bold text-foreground h-12">Patient Name</TableHead>
                      <TableHead className="font-bold text-foreground h-12">DOB</TableHead>
                      <TableHead className="font-bold text-foreground h-12">EMR ID</TableHead>
                      <TableHead className="font-bold text-foreground h-12">Clinic Name</TableHead>
                      <TableHead className="font-bold text-foreground h-12 text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-testid="pe-patient-table-body">
                    {patients.map((patient) => (
                      <TableRow 
                        key={patient.patient_program_id}
                        className="hover:bg-muted/30 transition-colors border-b border-border/50"
                        data-testid={`pe-patient-row-${patient.patient_program_id}`}
                      >
                        <TableCell className="font-semibold text-foreground py-4">
                          {patient.patient_full_name}
                        </TableCell>
                        <TableCell className="text-muted-foreground py-4">
                          {patient.dob || 'N/A'}
                        </TableCell>
                        <TableCell className="py-4">
                          <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold border border-primary/20">
                            {patient.emr_id || 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground py-4">
                          {patient.clinic_name || 'N/A'}
                        </TableCell>
                        <TableCell className="py-4 text-center">
                          <Button
                            onClick={() => navigate(`/es-dashboard?from=pe-dashboard&patientId=${patient.patient_id}&activityId=${patient.activity_id || ''}&workflowId=${patient.workflow_id || ''}`)}
                            className="bg-primary hover:bg-primary-hover text-primary-foreground h-9 px-4 text-sm font-medium"
                          >
                            <Edit className="h-4 w-4 mr-1.5" />
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {!isLoading && totalCount > 0 && (
          <div className="mt-6 bg-white border rounded-lg p-4 shadow-sm" data-testid="pe-pagination-container">
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              recordsPerPage={rowsPerPage}
              totalRecords={totalCount}
              startIndex={(currentPage - 1) * rowsPerPage}
              endIndex={Math.min(currentPage * rowsPerPage, totalCount)}
              onPageChange={handlePageChange}
              onRecordsPerPageChange={handleRecordsPerPageChange}
              data-testid="pe-pagination"
            />
          </div>
        )}
      </div>

      {/* Logout Confirmation Dialog */}
      <LogoutConfirmDialog
        open={showLogoutDialog}
        onOpenChange={setShowLogoutDialog}
        onConfirm={() => {
          setShowLogoutDialog(false);
          navigate('/', { replace: true });
        }}
        data-testid="pe-logout-dialog"
      />
    </div>
  );
};

export default PEDashboard;
